import { BaseElement } from 'base';
import { forwardUtm } from 'forward-utm';

class ActionButton extends BaseElement {
  #destination = null;

  constructor() {
    super();
    this.action = null;
    this.busy = false;
  }

  onConnect() {
    this.control = this.q('[data-action-control]');
    if (!this.iconStates && this.q('[data-icon-for="confirm"]'))
      this.iconStates = this.qa('[data-icon-for]').map(
        el => el.dataset.iconFor
      );
    this.setLoading(this.getAttribute('data-loading') === 'true');
    this.listen(this.control, 'click', this.onClick);
    this.wireDemo();
    this.#applyForwardedUtm();
  }

  #applyForwardedUtm() {
    if (this.dataset.forwardUtm !== 'true') return;
    if (this.control.tagName !== 'A') return;
    this.#destination ??= this.control.getAttribute('href');
    if (!this.#destination) return;

    const href = forwardUtm(
      this.#destination,
      window.location.search,
      window.location.origin
    );
    if (!href) {
      console.error(
        '[action-button] cannot forward utm params to:',
        this.#destination
      );
      return;
    }
    this.control.setAttribute('href', href);
  }

  wireDemo() {
    const delay = this.dataset.demoDelay;
    if (delay == null) return;
    const wait = () =>
      new Promise(resolve => setTimeout(resolve, Number(delay)));
    if (this.q('.btn__content-stack')) {
      this.confirmAction({ onChange: wait });
    } else if (this.q('[data-icon-for]')) {
      this.toggleIcon();
    } else {
      this.setAction(wait);
    }
  }

  setAction(action) {
    this.action = action;
  }

  toggleIcon({ states, labels, onChange } = {}) {
    this.iconStates = states?.length
      ? states
      : this.qa('[data-icon-for]').map(el => el.dataset.iconFor);
    this.iconLabels = labels;

    const initial =
      this.qa('[data-icon-for]').find(el => !el.hidden)?.dataset.iconFor ||
      this.iconStates[0];
    this.setIconState(initial);

    this.setAction(async () => {
      const previous = this.iconState;
      const next =
        this.iconStates[
          (this.iconStates.indexOf(previous) + 1) % this.iconStates.length
        ];
      this.setIconState(next);
      try {
        await onChange?.(next, this);
      } catch (error) {
        this.setIconState(previous);
        throw error;
      }
    });
    return this;
  }

  confirmAction({ duration = 1500, onChange } = {}) {
    this.iconStates = ['default', 'confirm'];
    this.setIconState('default');

    this.setAction(async () => {
      await onChange?.(this);
      this.setLoading(false);
      this.setIconState('confirm');
      await new Promise(resolve => setTimeout(resolve, duration));
      if (this.isConnected) this.setIconState('default');
    });
    return this;
  }

  setIconState(state) {
    if (!this.iconStates?.includes(state)) return;
    this.iconState = state;
    this.setAttribute('data-state', state);
    let target;
    this.qa('[data-icon-for]').forEach(el => {
      const active = el.dataset.iconFor === state;
      el.hidden = !active;
      if (active) target = el;
    });
    const label = this.iconLabels?.[state] ?? target?.dataset.label;
    if (label) {
      this.control.setAttribute('aria-label', label);
    } else {
      this.control.removeAttribute('aria-label');
    }
    if (this.control.hasAttribute('aria-pressed')) {
      this.control.setAttribute(
        'aria-pressed',
        state === this.iconStates[0] ? 'false' : 'true'
      );
    }
  }

  async onClick(event) {
    if (this.action) event.preventDefault();
    if (!this.action || this.busy || this.loading || this.isInert()) return;
    this.busy = true;
    this.setLoading(true);
    try {
      await this.action();
    } catch (error) {
      console.error('[action-button] action failed:', error);
    } finally {
      if (this.isConnected) this.setLoading(false);
      this.busy = false;
    }
  }

  isInert() {
    return (
      this.control.disabled ||
      this.control.getAttribute('aria-disabled') === 'true'
    );
  }

  setLoading(on) {
    this.loading = on;
    this.setAttribute('data-loading', on ? 'true' : 'false');
    if (on) {
      this.control.setAttribute('aria-busy', 'true');
    } else {
      this.control.removeAttribute('aria-busy');
    }
  }

  setDisabled(on) {
    if (this.control.tagName === 'A') {
      if (on) {
        this.control.setAttribute('aria-disabled', 'true');
        this.control.setAttribute('tabindex', '-1');
      } else {
        this.control.removeAttribute('aria-disabled');
        this.control.removeAttribute('tabindex');
      }
    } else {
      this.control.disabled = on;
    }
  }
}

if (!customElements.get('action-button'))
  customElements.define('action-button', ActionButton);
